/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { LogCategory, PanelRegion, ScenarioName } from '../../../logger';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { TemplateManagementCommandBar, TemplateManagementHeader, TemplateManagementList } from '../../components';
import { TemplateInstanceChangeStatusDialog } from '../../components/TemplateInstanceChangeStatusDialog';
import { TemplateManagementModal } from '../TemplateManagementModal';
import { getScrollablePaneStackItemStyles, getScrollablePaneStyles, getStackStyles } from './TemplateManagement.styles';

export const TemplateManagement: React.FC = (): JSX.Element => {
    const logger = getAppHost().getLogger();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    React.useEffect((): void => {
        logger.logTraceInfo(LogCategory.TemplateManagement, 'Rendered the template management page');
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    logger.useCreateScenarioEffect(
        ScenarioName.TemplateManagement,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        getAppHost().getPersonalAppEntryPoint(),
        'appBar',
        PanelRegion.Main,
        'templateManagement'
    );

    const templateManagementPage = React.useMemo((): JSX.Element => {
        return (
            <>
                <Stack styles={getStackStyles(theme)}>
                    <Stack.Item>
                        <TemplateManagementHeader />
                    </Stack.Item>
                    <Stack.Item>
                        <TemplateManagementCommandBar />
                    </Stack.Item>
                    <Stack.Item grow styles={getScrollablePaneStackItemStyles}>
                        <ScrollablePane styles={getScrollablePaneStyles}>
                            <TemplateManagementList />
                        </ScrollablePane>
                    </Stack.Item>
                </Stack>
                <TemplateManagementModal />
                <TemplateInstanceChangeStatusDialog />
            </>
        );
    }, [theme]);

    return <div>{templateManagementPage}</div>;
};
